import { zodResolver } from '@hookform/resolvers/zod';
import {
  type SaleRefundResource,
  SaleRefundsController_refund_v1,
  type SaleRefundsController_refund_v1Body,
  type SaleResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { instantNow } from '@/lib/clock';
import { requiredRefundReasonSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { formatPaise } from '@/lib/money';
import { newUuidv7 } from '@/lib/uuidv7';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';

/**
 * Refunding a sale from the back office: `POST /v1/sales/{id}/refund`.
 *
 * **A refund is a new record and a compensating entry in the books — never an
 * edit** (R-408, FR-441). The sale row is append-only on the server and its
 * ledger entry is immutable, so nothing here changes what was rung up: what is
 * written is a separate row pointing at the sale, plus the entry that reverses
 * it. The sale goes on being readable everywhere, marked (FR-447), which is what
 * keeps a day whose takings are short explicable. Any copy on this screen that
 * implied the sale had changed would be lying about what the books say.
 *
 * **The whole sale, or nothing.** There is no amount in the body and no way to
 * express a partial refund (CL-403a) — so there is no line picker here, and its
 * absence is the contract rather than a simplification of it.
 *
 * **No stock moves.** A refund records that goods came back; returning them to
 * stock is a separate milestone, and the panel below says so rather than letting
 * somebody assume the shelves have been corrected.
 *
 * ## Who may do this
 *
 * Anyone signed in who can reach the sale. `SaleRefundsController` declares
 * `@RequireRole()` with **no** arguments and `@RequireOutletScope()`, and says
 * why it is not a list of roles: CL-403 settles that anyone who may sell at an
 * outlet may refund a sale there, which is about *reach* rather than rank. So
 * there is no role check in this dialog and there must not be one — a screen
 * inventing a rank here would hide a control the API grants. The route this
 * dialog is reached from is narrower (`OWNER`, `MANAGER`), and that narrowing is
 * the route's own decision, argued in `route-table.tsx`.
 *
 * A sale outside the caller's reach answers `404`, byte for byte identical to one
 * that does not exist (FR-437, FR-128), and nothing here tells them apart.
 *
 * ## The reason is required here, and that is this screen's decision
 *
 * `RefundSaleDto` makes `reason` optional and the server accepts one without it.
 * This asks anyway, for the reason `VoidTicketDialog` asks: a back-office refund
 * is the one nobody will be able to reconstruct — made at a desk, possibly days
 * later, about a sale somebody else took — and money went back. **The till does
 * not insist**, deliberately, because there is a customer waiting for every
 * sentence typed at a counter.
 *
 * ## Three values minted once per mount, and each for its own reason
 *
 * - **`body.id` is the refund's own**, a fresh UUIDv7 per refund. It is what
 *   makes a resubmission recognisable: the same id sent again is answered `200`
 *   with the original outcome (FR-445), while a *different* id against a sale
 *   already refunded is `409 SALES_ALREADY_REFUNDED` (FR-446).
 * - **`Idempotency-Key` is per attempt**, so a retry after a dropped answer
 *   replays the first response instead of writing again.
 * - **`occurredAt` is minted here rather than at submit**, and that is not
 *   cosmetic: the key above fingerprints the body, so a second submit carrying a
 *   fresher timestamp would present one key with two payloads and earn
 *   `409 SYNC_IDEMPOTENCY_KEY_REUSED` — non-retryable, on the one screen where
 *   pressing again is the natural thing to do. It is recorded and never trusted
 *   to decide anything; the server keeps its own `recordedAt` alongside and reads
 *   the cut-off from the sale's business day (FR-008, FR-443).
 *
 * All three are held for as long as this form is mounted, which is exactly one
 * refund: the dialog unmounts when it closes, so the next one gets fresh values.
 *
 * ## The three outcomes, and which of them is an error
 *
 * | | |
 * | --- | --- |
 * | the same refund re-sent | `200`, the original outcome — **not** an error |
 * | a different refund on a refunded sale | `409 SALES_ALREADY_REFUNDED` |
 * | the sale's own day has closed | `409 SALES_DAY_CLOSED` |
 *
 * The first resolves through the same success path as a `201` and this screen
 * never says otherwise: the honest wording is true of either, the sale is
 * refunded. The other two are refusals, worded apart from each other in
 * `error-messages.ts` and worded there rather than here. Nothing below branches
 * on a status, and nothing below writes refusal prose.
 *
 * **Flattening them is the decision here, not the default.** `RequestExtras.onStatus`
 * would report which status carried the body, and this screen deliberately does
 * not ask — the middle row above is why. Unlike the void route, this one keys
 * its replay on the refund's own id, so a `200` can *only* be this form's own
 * resubmission after a dropped answer; somebody else refunding first is the
 * `409` on row two, already worded. So the `200` a reader could reach carries no
 * news: it is their own retry of their own refund, and "the sale is refunded" is
 * what they were waiting to hear either way.
 */

const refundSaleSchema = z.object({ reason: requiredRefundReasonSchema });

type RefundSaleValues = z.infer<typeof refundSaleSchema>;

export function RefundSaleDialog({
  sale,
  onClose,
  onRefunded,
}: {
  /** The sale being reversed, or `null` when none is. */
  sale: SaleResource | null;
  onClose: () => void;
  /** Handed the refund, which carries the sale as it now stands — marked. */
  onRefunded: (refund: SaleRefundResource) => void;
}) {
  return (
    <Dialog
      open={sale !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {/* A child, so closing unmounts it: the next refund carries a fresh
            refund id, a fresh key and a fresh instant rather than replaying
            this one. */}
        {sale !== null && <RefundSaleForm sale={sale} onRefunded={onRefunded} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function RefundSaleForm({
  sale,
  onClose,
  onRefunded,
}: {
  sale: SaleResource;
  onClose: () => void;
  onRefunded: (refund: SaleRefundResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(refundSaleSchema),
    mode: 'onTouched',
    defaultValues: { reason: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // Minted lazily by `useState`'s initialiser form so a re-render does not mint
  // a second and throw it away — and held for the life of this form, which is
  // one refund. The reasoning for all three is in the header.
  const [refundId] = useState(newUuidv7);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [occurredAt] = useState(instantNow);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async ({ reason }: RefundSaleValues) => {
    // Belt and braces with the disabled button. A second submit carries the same
    // key and the same payload so the API replays rather than writing twice, but
    // it would still put this form into a second in-flight state for nothing.
    if (pending) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client; nothing about this
      // route is written down here. In particular there is no `amountPaise` —
      // a refund is the whole sale and there is no field for a part of it — and
      // no `actorId`, because the person is resolved from the authenticated
      // principal and the API refuses a body naming one.
      const body: SaleRefundsController_refund_v1Body = { id: refundId, reason, occurredAt };

      onRefunded(
        await client.request(SaleRefundsController_refund_v1, {
          path: { id: sale.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error: unknown) {
      // Every refusal this route can raise is worded from its `code` in
      // `error-messages.ts` — including the two that must read differently from
      // each other: `SALES_ALREADY_REFUNDED` (the money went back once already)
      // and `SALES_DAY_CLOSED` (too late here; an owner must reopen that day).
      setFormError(messageForFailure(error));

      // The reason is left as it was typed. A refusal about the day or about a
      // refund that already exists has nothing to do with the words, and
      // clearing them would make somebody write the sentence twice.
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Refund <span className="font-mono numeric">{sale.receiptNumber}</span>?
        </DialogTitle>
        <DialogDescription>
          <span className="font-mono text-foreground numeric">{formatPaise(sale.totalPaise)}</span>{' '}
          goes back to the customer. A sale can only be refunded once, and this cannot be undone.
        </DialogDescription>
      </DialogHeader>

      {/* `text-foreground` on `bg-muted` — the muted-on-muted pairing misses AA
          and is the one a quiet inset panel invites. */}
      <div className="space-y-3 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
        <p>
          This writes a refund against the sale and a matching entry in the books. The sale itself
          is not changed: it stays in the day&rsquo;s record, marked, so the takings still add up at
          close.
        </p>
        <p>
          The whole sale is reversed — there is no part refund. Nothing is returned to stock here;
          the goods are counted back separately.
        </p>
        {sale.tax.status === 'TAXED' && (
          <p>
            Its tax invoice, <span className="font-mono numeric">{sale.tax.invoiceNumber}</span>, is
            reversed with it in full.
          </p>
        )}
      </div>

      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Reason
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Returned unopened"
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Asked for here even though the server does not require it: money went back, and
                  whoever reconciles this day was not standing at the counter when it did.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Above the submit and it stays there — a toast vanishes, and somebody
              who looked away has no way to get it back. */}
          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            {/* `destructive`, which this system reserves for the irreversible.
                The amount is on the button: it is the figure the person
                confirming is about to hand back. */}
            <Button type="submit" variant="destructive" disabled={pending}>
              {pending ? 'Refunding…' : `Refund ${formatPaise(sale.totalPaise)}`}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
